import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Play,
  Send,
  MapPin,
  Briefcase,
  Clock,
  User,
  CheckCircle,
  Sparkles,
  Camera,
  Share2,
  Building2,
  ChevronRight,
  Youtube,
  Calendar,
  Mail,
  Phone,
  MessageCircle,
  Star,
  Users,
  Zap,
  Award,
  AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { cubeLogo, trustedBrands, celebrityImages, landscapeVideos, shortsVideos, aboutData } from "../data/mock";

// Job Positions
const jobPositions = [
  {
    id: "social-media-executive",
    title: "Social Media Manager",
    icon: Share2,
    color: "from-blue-500 to-cyan-500",
    salaryRange: "₹6,000 - ₹12,000",
    workType: "Remote + Office visits on shoot days",
    description: "Manage social media accounts, create content, and be present on shoot days for execution.",
    responsibilities: [
      "Manage multiple client social media accounts",
      "Create and schedule engaging content",
      "Attend office on shoot days & meetings",
      "Execute shoots and coordinate with team",
      "Monitor trends and analytics"
    ],
    skills: ["Social Media Platforms", "Content Writing", "Basic Design (Canva)", "Photography/Videography", "Communication"]
  },
  {
    id: "cinematographer-video-editor",
    title: "Cinematographer & Video Editor",
    icon: Camera,
    color: "from-purple-500 to-pink-500",
    salaryRange: "₹6,000 - ₹15,000",
    workType: "Remote + Office visits on shoot days",
    description: "Shoot professional videos and handle complete editing — work from home with office visits for shoots.",
    responsibilities: [
      "Shoot professional video content on location",
      "Edit videos for social media & YouTube",
      "Work remotely on editing tasks",
      "Attend office for shoots & meetings",
      "Color grading and sound design"
    ],
    skills: ["Premiere Pro", "After Effects", "Camera Operation", "Lighting", "Color Grading"]
  }
];

// Perks
const perks = [
  { icon: Building2, text: "Remote Job" },
  { icon: Clock, text: "Flexible Hours" },
  { icon: Calendar, text: "Office on Shoot Days" },
  { icon: Users, text: "Job & Internship" }
];

// Why Join Cube
const whyJoinCube = [
  { icon: Zap, title: "Fast Growth", desc: "Learn and grow rapidly in a dynamic environment" },
  { icon: Award, title: "Real Projects", desc: "Work with top brands and celebrities" },
  { icon: Users, title: "Great Team", desc: "Collaborate with passionate creatives" },
  { icon: Star, title: "Recognition", desc: "Your work gets seen by thousands" }
];

// Video Card Component
const VideoCard = ({ video, isShort = false }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/${isShort ? '0' : 'maxresdefault'}.jpg`;

  if (playing) {
    return (
      <div className={`relative ${isShort ? 'aspect-[9/16]' : 'aspect-video'} rounded-xl overflow-hidden bg-black`}>
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  return (
    <div 
      className={`relative ${isShort ? 'aspect-[9/16]' : 'aspect-video'} rounded-xl overflow-hidden cursor-pointer group bg-black border border-white/10 hover:border-red-500/50 transition-all`}
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg"
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-white text-xs font-medium line-clamp-2">{video.title}</p>
      </div>
    </div>
  );
};

export default function CubeCareersPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    position: "",
    employmentType: "",
    experience: "",
    availability: "",
    expectation: "",
    portfolio: "",
    coverLetter: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === "phone" && sameAsPhone) {
      setFormData(prev => ({ ...prev, [name]: value, whatsapp: value }));
    }
  };

  const handleSameAsPhone = (checked) => {
    setSameAsPhone(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, whatsapp: prev.phone }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/careers/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setSubmitted(true);
    } catch (err) {
      console.error("Application submission error:", err);
      setError(err.message || "Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Select only feedback videos
  const feedbackVideos = landscapeVideos.filter(v => v.type === 'feedback').slice(0, 4);
  const feedbackShorts = shortsVideos.filter(v => v.industry === 'Client Feedback' || v.title.includes('Feedback')).slice(0, 6);

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Application Submitted!</h2>
          <p className="text-gray-400 mb-6">
            Thanks for applying to join Cube Multimedia. We'll review your application and get back to you soon.
          </p>
          <Link to="/">
            <Button className="bg-[#dc2626] hover:bg-[#b91c1c]">
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-8 sm:h-10 md:h-12" />
          </Link>
          <a href="#apply-form">
            <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-xs sm:text-sm px-4 py-2">
              Apply Now
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 pb-10 sm:pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs sm:text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              We're Hiring
            </span>

            {/* Remote Job Highlight */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 text-emerald-400 text-sm sm:text-base font-semibold mb-6 ml-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Work from Home
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Join <span className="text-[#dc2626]">Cube Multimedia</span>
            </h1>
            
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto mb-6">
              Eastern India's Leading Digital Marketing Agency. Work with top brands, celebrities, and a passionate team of creatives.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-8">
              {aboutData.stats.slice(0, 4).map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {perks.map((perk, index) => (
                <div key={index} className="flex items-center gap-1.5 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
                  <perk.icon className="w-4 h-4 text-red-400" />
                  <span className="text-xs">{perk.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Important Budget Notice */}
      <section className="px-4 py-6 sm:py-8 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-y border-amber-500/20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 rounded-full text-amber-300 text-xs font-medium mb-4">
              <AlertTriangle className="w-4 h-4" />
              Pilot Project - Limited Budget
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-3 text-white">
              Before You Apply... <span className="text-amber-400">A Friendly Heads Up!</span>
            </h3>
            <p className="text-sm text-gray-300 max-w-2xl mx-auto mb-4">
              We're launching a <strong>new pilot project</strong> and hiring specifically for it. 
              These are <strong>remote positions</strong> — work from home, but join us at office on shoot days & meetings.
              Our regular salaries are much higher, but for this project:
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 mb-4">
              <div className="px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-blue-400 font-semibold text-sm">Social Media Manager</p>
                <p className="text-white font-bold text-lg">₹6,000 - ₹12,000<span className="text-xs text-gray-400">/month</span></p>
                <p className="text-[10px] text-gray-500 mt-1">Remote + Office on shoot days</p>
              </div>
              <div className="px-4 py-3 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <p className="text-purple-400 font-semibold text-sm">Cinematographer & Editor</p>
                <p className="text-white font-bold text-lg">₹6,000 - ₹15,000<span className="text-xs text-gray-400">/month</span></p>
                <p className="text-[10px] text-gray-500 mt-1">Remote + Office on shoot days</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 max-w-xl mx-auto">
              If your salary expectation is ₹30,000+ or above, this role isn't for you right now. 
              Let's not waste each other's time! But if you're excited to learn, grow, and build something new with us — we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="px-4 py-10 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Open <span className="text-[#dc2626]">Positions</span>
            </h2>
            <p className="text-gray-400 text-sm">Choose your role and start your creative journey</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {jobPositions.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-5 sm:p-6 bg-[#141414] rounded-2xl border transition-all cursor-pointer ${
                  selectedPosition === job.id 
                    ? 'border-red-500 ring-2 ring-red-500/20' 
                    : 'border-white/10 hover:border-white/20'
                }`}
                onClick={() => {
                  setSelectedPosition(job.id);
                  setFormData(prev => ({ ...prev, position: job.title }));
                }}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${job.color} flex items-center justify-center mb-4`}>
                  <job.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2">{job.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{job.description}</p>
                
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-300 mb-2">Key Responsibilities:</p>
                  <ul className="space-y-1">
                    {job.responsibilities.slice(0, 3).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                        <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {job.skills.slice(0, 4).map((skill, i) => (
                    <span key={i} className="px-2 py-1 bg-white/5 rounded text-[10px] text-gray-300">
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">Job & Internship</span>
                    <span className="text-xs text-green-400 font-medium">{job.salaryRange}/month</span>
                  </div>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {job.workType}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Cube */}
      <section className="px-4 py-10 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Why Join <span className="text-[#dc2626]">Cube?</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {whyJoinCube.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-[#141414] rounded-xl border border-white/10 text-center"
              >
                <item.icon className="w-8 h-8 mx-auto mb-3 text-red-400" />
                <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form - MOVED UP */}
      <section id="apply-form" className="px-4 py-10 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Apply <span className="text-[#dc2626]">Now</span>
            </h2>
            <p className="text-gray-400 text-sm">Fill out the form to join our team</p>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-[#141414] rounded-2xl p-5 sm:p-8 border border-white/10"
          >
            {/* Name */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <User className="w-4 h-4 inline mr-2" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Mail className="w-4 h-4 inline mr-2" />
                Email ID *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="your.email@example.com"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            {/* Phone */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            {/* WhatsApp */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <MessageCircle className="w-4 h-4 inline mr-2" />
                WhatsApp Number *
              </label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="sameAsPhone"
                  checked={sameAsPhone}
                  onChange={(e) => handleSameAsPhone(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="sameAsPhone" className="text-xs text-gray-400 cursor-pointer">
                  Same as phone number
                </label>
              </div>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleInputChange}
                required
                disabled={sameAsPhone}
                placeholder="Enter your WhatsApp number"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors disabled:opacity-50"
              />
            </div>

            {/* Position */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Briefcase className="w-4 h-4 inline mr-2" />
                Position Applying For *
              </label>
              <select
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select position</option>
                <option value="Social Media Manager" className="bg-[#141414]">Social Media Manager</option>
                <option value="Cinematographer & Video Editor" className="bg-[#141414]">Cinematographer & Video Editor</option>
              </select>
            </div>

            {/* Employment Type */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Users className="w-4 h-4 inline mr-2" />
                Employment Type *
              </label>
              <select
                name="employmentType"
                value={formData.employmentType}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select type</option>
                <option value="Job" className="bg-[#141414]">Job</option>
                <option value="Internship" className="bg-[#141414]">Internship</option>
              </select>
            </div>

            {/* Experience */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Award className="w-4 h-4 inline mr-2" />
                Experience *
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select experience</option>
                <option value="Fresher" className="bg-[#141414]">Fresher (No experience)</option>
                <option value="0-6 months" className="bg-[#141414]">0-6 months</option>
                <option value="6-12 months" className="bg-[#141414]">6-12 months</option>
                <option value="1-2 years" className="bg-[#141414]">1-2 years</option>
                <option value="2+ years" className="bg-[#141414]">2+ years</option>
              </select>
            </div>

            {/* Availability */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                <Clock className="w-4 h-4 inline mr-2" />
                When can you start? *
              </label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select availability</option>
                <option value="Immediate" className="bg-[#141414]">Immediate</option>
                <option value="Within 1 week" className="bg-[#141414]">Within 1 week</option>
                <option value="Within 2 weeks" className="bg-[#141414]">Within 2 weeks</option>
                <option value="Within 1 month" className="bg-[#141414]">Within 1 month</option>
              </select>
            </div>

            {/* Salary Expectation */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                Salary Expectation (Monthly) *
              </label>
              <select
                name="expectation"
                value={formData.expectation}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#141414]">Select your expectation</option>
                <option value="₹6,000 - ₹8,000" className="bg-[#141414]">₹6,000 - ₹8,000</option>
                <option value="₹8,000 - ₹10,000" className="bg-[#141414]">₹8,000 - ₹10,000</option>
                <option value="₹10,000 - ₹12,000" className="bg-[#141414]">₹10,000 - ₹12,000</option>
                <option value="₹12,000 - ₹15,000" className="bg-[#141414]">₹12,000 - ₹15,000 (Cinematographer only)</option>
              </select>
              <p className="text-[10px] text-amber-400 mt-1.5">⚠️ Budget: Social Media ₹6K-12K | Cinematographer ₹6K-15K. Higher expectations won't be considered.</p>
            </div>

            {/* Portfolio */}
            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                Portfolio / Work Samples
              </label>
              <input
                type="url"
                name="portfolio"
                value={formData.portfolio}
                onChange={handleInputChange}
                placeholder="Google Drive / Behance / Instagram link"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              />
              <p className="text-[10px] text-gray-500 mt-1">Share Google Drive link with "Anyone with the link" access</p>
            </div>

            {/* Cover Letter */}
            <div className="mb-6">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-300">
                Why do you want to join Cube?
              </label>
              <textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                rows={3}
                placeholder="Tell us briefly about yourself and why you're interested..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors resize-none"
              />
            </div>

            {/* Support */}
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-between">
              <p className="text-xs text-green-300">
                <strong>Questions?</strong> Contact us
              </p>
              <a 
                href="https://wa.me/918017491858" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-white text-xs font-medium transition-all"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-50 text-white py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Application
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </section>

      {/* Client Testimonials - MOVED DOWN */}
      <section className="px-4 py-10 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              What Our <span className="text-[#dc2626]">Clients Say</span>
            </h2>
            <p className="text-gray-400 text-sm">Real feedback from businesses we've helped grow</p>
          </div>

          {/* Landscape Videos */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {feedbackVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {/* Shorts */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
            {feedbackShorts.map((video) => (
              <VideoCard key={video.id} video={video} isShort />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery - Our Work */}
      <section className="px-4 py-10 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              Our <span className="text-[#dc2626]">Work & Events</span>
            </h2>
            <p className="text-gray-400 text-sm">From brand shoots to celebrity events</p>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {celebrityImages.slice(0, 12).map((img, index) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="aspect-square rounded-lg overflow-hidden"
              >
                <img
                  src={img.src}
                  alt={`Cube work ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Brands */}
      <section className="px-4 py-10 sm:py-12">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-gray-400 text-sm mb-6">Brands that trust us</p>
          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10">
            {trustedBrands.slice(0, 8).map((brand, index) => (
              <img
                key={index}
                src={brand.logo}
                alt={brand.name}
                className="h-8 sm:h-10 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-6 sm:py-8 border-t border-white/10">
        <div className="max-w-5xl mx-auto text-center">
          <img src={cubeLogo} alt="Cube Multimedia" className="h-8 mx-auto mb-4" />
          <p className="text-xs text-gray-500 mb-2">Eastern India's Leading Digital Marketing Agency</p>
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <a href="https://cubemultimedia.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Website</a>
          </div>
          <p className="text-[10px] text-gray-600 mt-4">© 2026 Cube Multimedia. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
